/**
 * Whether sign-up comes first.
 *
 * The server holds the switch (SIGNUP_FIRST_ONBOARDING) and says where it
 * stands at GET /api/onboarding/entry. The client draws the on state from
 * its first paint and changes only when the server says off, so production,
 * which runs with it on, never shows the address box for a frame and then
 * swaps it. The answer is kept for the tab: one request, then none.
 *
 * A plain fetch, not the API service. The header that reads this is built
 * into every static page, where there is no session and no need of one, and
 * the endpoint is public.
 *
 * On a static page the header is rendered at build time, where there is no
 * sessionStorage: the read fails quietly and the build draws the on state,
 * which is the one production serves.
 *
 * One answer for every reader. The header, the hero and the page under them
 * all read this and mount together, so the answer lives here, in the module,
 * not in each of them: one request however many ask, and when it comes back
 * every reader is told at once. Two readers can never disagree.
 *
 * Two ways to read it. useSignupFirst draws on until told otherwise, for a
 * page that has to draw something at once. useSignupFirstKnown says nothing
 * until the server has answered, for a page that must not act on a guess.
 */
import { useEffect, useSyncExternalStore } from 'react'

export const SIGNUP_FIRST_CACHE_KEY = 'ss_signup_first'

function readCached(): boolean | null {
  try {
    const raw = sessionStorage.getItem(SIGNUP_FIRST_CACHE_KEY)
    return raw === '1' ? true : raw === '0' ? false : null
  } catch {
    return null
  }
}

function writeCached(value: boolean): void {
  try {
    sessionStorage.setItem(SIGNUP_FIRST_CACHE_KEY, value ? '1' : '0')
  } catch {
    /* storage unavailable: the next page asks again */
  }
}

/**
 * The server's answer, or null when it gave none that can be read as a yes
 * or a no.
 *
 * Not everything that comes back from this address is the server's answer:
 * a rate limiter replies in plain text, and staging's password page replies
 * 200 with HTML. Only { success: true, data: { signupFirst: <boolean> } }
 * counts. Anything else is no answer, whatever it seems to say.
 */
async function askServer(): Promise<boolean | null> {
  const response = await fetch('/api/onboarding/entry', { headers: { Accept: 'application/json' } })
  if (!response.ok) return null
  let body: unknown
  try {
    body = await response.json()
  } catch {
    return null
  }
  if (typeof body !== 'object' || body === null) return null
  const { success, data } = body as { success?: unknown; data?: unknown }
  if (success !== true || typeof data !== 'object' || data === null) return null
  const { signupFirst } = data as { signupFirst?: unknown }
  return typeof signupFirst === 'boolean' ? signupFirst : null
}

/* What the server said, once it has. Kept here as well as in storage, so a tab that blocks storage still obeys it. */
let answer: boolean | null = null
/* The request every reader shares while it is open. */
let asking: Promise<void> | null = null
/* Goes up each time the answer is forgotten, so a request made before that cannot bring the old answer back. */
let forgotten = 0
/* The server was asked and gave no answer that can be read. Nothing is waited for any more: see getKnownSnapshot. */
let noAnswer = false
const readers = new Set<() => void>()

function known(): boolean | null {
  if (answer === null) answer = readCached()
  return answer
}

function tellReaders(): void {
  readers.forEach((reader) => reader())
}

function subscribe(reader: () => void): () => void {
  readers.add(reader)
  return () => {
    readers.delete(reader)
  }
}

function getSnapshot(): boolean {
  return known() ?? true
}

/*
  A page built ahead of time is built as on, and the browser's first draw of
  it has to match what was built. React reads this for that first draw, then
  getSnapshot, so a tab that remembers "off" settles a moment later instead
  of tearing the built page.
*/
function getServerSnapshot(): boolean {
  return true
}

/*
  For a reader that waits to be told: null until the server has answered.

  A request that comes back with no answer ends the wait as well. The plain
  hook keeps drawing on in that case, so this settles on too, and the two
  readers agree. It is not remembered: the next reader to mount asks again,
  and a real answer, whenever one comes, replaces it.
*/
function getKnownSnapshot(): boolean | null {
  return known() ?? (noAnswer ? true : null)
}

/* Nothing is known where a page is built ahead of time, and the first draw in the browser has to match that. */
function getKnownServerSnapshot(): boolean | null {
  return null
}

/* The request is over and said nothing: whoever was waiting stops waiting. */
function gotNoAnswer(): void {
  noAnswer = true
  tellReaders()
}

function ask(): void {
  if (asking !== null || known() !== null) return
  const asked = forgotten
  asking = askServer()
    .then((reply) => {
      if (asked !== forgotten) return
      if (reply === null) {
        gotNoAnswer()
        return
      }
      answer = reply
      writeCached(reply)
      tellReaders()
    })
    .catch(() => {
      /* no answer is not a no: the page stays as it was drawn */
      if (asked === forgotten) gotNoAnswer()
    })
    .finally(() => {
      if (asked === forgotten) asking = null
    })
}

/**
 * Forget the answer, for when the server contradicts it.
 *
 * The answer is kept for the tab, so a tab left open across a flip of the
 * switch holds a stale one. A caller that is refused in a way only the other
 * state explains calls this: every reader goes back to the default, and the
 * server is asked again.
 *
 * Asked at once when somebody is still reading. A page that forgets and
 * stays where it is has no new reader to mount, so without this its readers
 * would sit on the default for good. With nobody reading there is nobody to
 * tell, and the next reader to mount asks.
 */
export function forgetSignupFirst(): void {
  try {
    sessionStorage.removeItem(SIGNUP_FIRST_CACHE_KEY)
  } catch {
    /* storage unavailable: there was nothing kept there to forget */
  }
  answer = null
  asking = null
  noAnswer = false
  forgotten += 1
  tellReaders()
  if (readers.size > 0) ask()
}

export function useSignupFirst(): boolean {
  const signupFirst = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  useEffect(() => {
    ask()
  }, [])

  return signupFirst
}

/**
 * The switch, for a page that must not act before it is known.
 *
 * Null until the server has answered; then its answer. A page that would
 * ask the server for something only one state has (the account funnel, on
 * the growth board) reads this and waits, instead of guessing on and asking
 * for what a switched-off server cannot give.
 *
 * It never waits for good. A request that comes back with no answer settles
 * it on, which is where useSignupFirst stands with no answer.
 *
 * `wanted` is for a page that has a use for the answer only some of the
 * time (the page detail, which needs it only for an account with no plan).
 * While it is false the server is not asked on this reader's account. What
 * the tab already knows is still read, and so is an answer another reader
 * asked for.
 */
export function useSignupFirstKnown(wanted = true): boolean | null {
  const signupFirst = useSyncExternalStore(subscribe, getKnownSnapshot, getKnownServerSnapshot)

  useEffect(() => {
    if (wanted) ask()
  }, [wanted])

  return signupFirst
}
